import {
	Bot,
	Coins,
	Eye,
	MousePointerClick,
	Search,
	Sparkles,
	Target,
	TrendingUp,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import {
	Bar,
	BarChart,
	CartesianGrid,
	ComposedChart,
	Legend,
	Line,
	LineChart,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts";
import { api } from "../api/client";
import { SocialTraffic } from "../components/flows/SocialTraffic";
import { PageHeader } from "../components/layout/PageHeader";

// ---------------------------------------------------------------------------
// Types — mirror MetricsDashboard in admin/apps/server/src/metrics/dashboard.ts
// ---------------------------------------------------------------------------
interface MetricDay {
	date: string;
	gscImpressions: number | null;
	gscClicks: number | null;
	gscCtr: number | null;
	gscAvgPosition: number | null;
	gscUrls: number | null;
	paClicks: number | null;
	paConversions: number | null;
	paRevenue: number | null;
	paCommission: number | null;
	paCancelled: number | null;
}

interface MetricTotals {
	days: number;
	gscImpressions: number;
	gscClicks: number;
	gscCtr: number;
	gscAvgPosition: number;
	gscUrlsPeak: number;
	paClicks: number;
	paConversions: number;
	paRevenue: number;
	paCommission: number;
	paCancelled: number;
	conversionRate: number;
	valuePerClick: number;
}

interface WeekOverWeek {
	gscThrough: string | null;
	paThrough: string | null;
	gscImpressions: number | null;
	gscClicks: number | null;
	gscAvgPosition: number | null;
	paClicks: number | null;
	paConversions: number | null;
	paCommission: number | null;
	valuePerClick: number | null;
}

interface MetricsResponse {
	items: MetricDay[];
	total: number;
	page: number;
	totalPages: number;
	range: { from: string; to: string };
	totals: MetricTotals;
	summary: { summary: string; generatedAt: string } | null;
	weekOverWeek: WeekOverWeek | null;
	generatedAt: string;
}

const RANGES = [
	{ label: "30d", days: 30 },
	{ label: "60d", days: 60 },
	{ label: "90d", days: 90 },
	{ label: "180d", days: 180 },
	{ label: "1y", days: 365 },
];

const fmt = (n: number) => n.toLocaleString("da-DK");
const kr = (n: number) =>
	`${n.toLocaleString("da-DK", { minimumFractionDigits: 0, maximumFractionDigits: 0 })} kr`;
// Eden Treaty revives anything date-shaped in a response — including a bare "2026-08-21" — into a
// Date object, so formatting these as strings server-side is not enough to keep one out of JSX.
const isoDay = (value: string | Date): string => {
	if (!(value instanceof Date)) return String(value).slice(0, 10);
	// A date-only string parses as UTC midnight, so read it back in UTC or a viewer west of
	// Greenwich sees every row shifted a day earlier.
	const m = String(value.getUTCMonth() + 1).padStart(2, "0");
	const d = String(value.getUTCDate()).padStart(2, "0");
	return `${value.getUTCFullYear()}-${m}-${d}`;
};
const shortDate = (iso: string | Date) =>
	new Date(`${isoDay(iso)}T00:00:00`).toLocaleDateString("da-DK", {
		day: "2-digit",
		month: "short",
	});
const fmtDateTime = (s: string | Date) =>
	new Date(s).toLocaleString("da-DK", {
		day: "2-digit",
		month: "short",
		hour: "2-digit",
		minute: "2-digit",
	});

/**
 * Week-over-week marker. `lowerIsBetter` flips the colour for average position, where a drop
 * from 9.6 to 8.1 is an improvement.
 */
const Delta = ({
	value,
	lowerIsBetter = false,
}: {
	value: number | null | undefined;
	lowerIsBetter?: boolean;
}) => {
	if (value === null || value === undefined) return null;
	const flat = Math.abs(value) < 0.05;
	const good = lowerIsBetter ? value < 0 : value > 0;
	const tone = flat
		? "text-slate-400"
		: good
			? "text-emerald-600"
			: "text-danger";
	return (
		<span
			className={`text-[11px] font-semibold ${tone}`}
			title="vs the previous 7 days"
		>
			{flat ? "→" : value > 0 ? "▲" : "▼"}{" "}
			{Math.abs(value).toLocaleString("da-DK", { maximumFractionDigits: 1 })}%
		</span>
	);
};

const StatCard = ({
	icon: Icon,
	label,
	value,
	sub,
	delta,
	lowerIsBetter,
}: {
	icon: typeof Eye;
	label: string;
	value: string;
	sub?: string;
	delta?: number | null;
	lowerIsBetter?: boolean;
}) => (
	<div className="min-w-0 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
		<p className="flex items-center gap-2 font-medium text-[0.7rem] text-zinc-500 uppercase tracking-[0.14em]">
			<Icon className="h-3.5 w-3.5 text-zinc-400" />
			{label}
		</p>
		<div className="mt-3 flex items-baseline gap-2">
			<p className="tnum truncate font-semibold text-[26px] text-ink leading-none">
				{value}
			</p>
			<Delta value={delta} lowerIsBetter={lowerIsBetter} />
		</div>
		{sub && <p className="mt-2 text-[11px] text-zinc-400">{sub}</p>}
	</div>
);

const Panel = ({
	title,
	hint,
	action,
	children,
}: {
	title: string;
	hint?: string;
	action?: React.ReactNode;
	children: React.ReactNode;
}) => (
	<div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
		<div className="mb-4 flex items-baseline justify-between gap-3">
			<h3 className="text-sm font-semibold text-slate-700">{title}</h3>
			<div className="flex items-baseline gap-3">
				{hint && <span className="text-[11px] text-slate-400">{hint}</span>}
				{action}
			</div>
		</div>
		{children}
	</div>
);

const axis = { tick: { fontSize: 11 }, stroke: "#94a3b8" };

const Metrics = () => {
	const [days, setDays] = useState(30);
	const [data, setData] = useState<MetricsResponse | null>(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(false);
	const [regenerating, setRegenerating] = useState(false);

	const load = useCallback(async () => {
		setLoading(true);
		setError(false);
		try {
			const { data: raw } = await api.metrics.daily.get({
				query: { days: String(days) },
			});
			setData(raw ? (raw as unknown as MetricsResponse) : null);
		} catch {
			setError(true);
		} finally {
			setLoading(false);
		}
	}, [days]);

	useEffect(() => {
		load();
	}, [load]);

	// The "summary" phase re-reads what is already stored and re-prompts Ollama — no Partner-ads
	// call, and none of the BigQuery spend a full /metrics/run incurs. Slow enough (a cloud LLM
	// round trip) to need its own pending state.
	const regenerateSummary = useCallback(async () => {
		setRegenerating(true);
		try {
			await api.metrics.run.post({ phase: "summary" });
			await load();
		} catch {
			setError(true);
		} finally {
			setRegenerating(false);
		}
	}, [load]);

	const items = data?.items ?? [];
	const totals = data?.totals;
	const wow = data?.weekOverWeek ?? undefined;
	// Bot days stay in the raw click chart (the clicks really happened) but are drawn in red so
	// nobody reads a scraper spike as growth.
	const chart = items.map((d) => ({ ...d, label: shortDate(d.date) }));

	if (loading && !data) {
		return (
			<div className="flex items-center justify-center py-32">
				<div className="h-8 w-8 animate-spin rounded-full border-3 border-primary/20 border-t-primary" />
			</div>
		);
	}

	return (
		<div className="space-y-6">
			<PageHeader
				kicker="Menu · SEO & affiliate"
				title="SEO & affiliate."
				qualifier="Search and commission, side by side."
				description={
					<p>
						Google Search Console and Partner-ads, one row per day
						{data &&
							` — ${isoDay(data.range.from)} to ${isoDay(data.range.to)}`}
					</p>
				}
				actions={
					<div className="flex items-center gap-1 rounded-lg border border-slate-200 bg-white p-1">
						{RANGES.map((r) => (
							<button
								key={r.days}
								type="button"
								onClick={() => setDays(r.days)}
								className={`rounded-md px-3 py-1.5 text-[13px] font-medium transition-colors ${
									days === r.days
										? "bg-primary text-white"
										: "text-slate-500 hover:bg-slate-100"
								}`}
							>
								{r.label}
							</button>
						))}
					</div>
				}
			/>

			{error && (
				<div className="rounded-xl border border-danger/20 bg-danger/5 px-4 py-3 text-sm text-danger">
					Couldn't load metrics.
				</div>
			)}

			{/* Totals */}
			<div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
				<StatCard
					icon={Eye}
					label="Search impressions"
					delta={wow?.gscImpressions}
					value={totals ? fmt(totals.gscImpressions) : "—"}
					sub={totals ? `${fmt(totals.gscUrlsPeak)} URLs at peak` : undefined}
				/>
				<StatCard
					icon={Search}
					label="Search clicks"
					delta={wow?.gscClicks}
					value={totals ? fmt(totals.gscClicks) : "—"}
					sub={totals ? `${totals.gscCtr}% CTR` : undefined}
				/>
				<StatCard
					icon={TrendingUp}
					label="Avg. position"
					delta={wow?.gscAvgPosition}
					lowerIsBetter
					value={totals ? String(totals.gscAvgPosition) : "—"}
					sub="Impression-weighted"
				/>
				<StatCard
					icon={MousePointerClick}
					label="Outbound clicks"
					delta={wow?.paClicks}
					value={totals ? fmt(totals.paClicks) : "—"}
				/>
				<StatCard
					icon={Target}
					label="Conversions"
					delta={wow?.paConversions}
					value={totals ? fmt(totals.paConversions) : "—"}
					sub={totals ? `${totals.conversionRate}% of clicks` : undefined}
				/>
				<StatCard
					icon={Coins}
					label="Commission"
					delta={wow?.paCommission}
					value={totals ? kr(totals.paCommission) : "—"}
					sub={totals ? `${kr(totals.paRevenue)} order value` : undefined}
				/>
				<StatCard
					icon={Coins}
					label="Value per click"
					delta={wow?.valuePerClick}
					value={totals ? `${totals.valuePerClick.toFixed(2)} kr` : "—"}
					sub="Bot days excluded"
				/>
				<StatCard
					icon={Bot}
					label="Cancelled"
					value={totals ? kr(totals.paCancelled) : "—"}
					sub="Commission reversed"
				/>
			</div>

			{/* AI summary — always the most recently generated one */}
			<Panel
				title="Latest summary"
				hint={
					data?.summary
						? `generated ${fmtDateTime(data.summary.generatedAt)}`
						: undefined
				}
				action={
					<button
						type="button"
						onClick={regenerateSummary}
						disabled={regenerating}
						className="rounded-md border border-slate-200 px-2.5 py-1 text-[11px] font-medium text-slate-500 transition-colors hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
					>
						{regenerating ? "Generating…" : "Regenerate"}
					</button>
				}
			>
				{data?.summary ? (
					<div className="flex gap-3">
						<Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
						<p className="whitespace-pre-line text-[13px] leading-relaxed text-slate-700">
							{data.summary.summary}
						</p>
					</div>
				) : (
					<p className="text-[13px] text-slate-400">
						No summary generated yet — the daily job writes one at 13:00.
					</p>
				)}
			</Panel>

			{/* Search Console volume */}
			<Panel title="Search Console impressions & clicks">
				<ResponsiveContainer width="100%" height={280}>
					<LineChart
						data={chart}
						margin={{ top: 5, right: 20, bottom: 5, left: -10 }}
					>
						<CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
						<XAxis dataKey="label" {...axis} />
						<YAxis yAxisId="left" {...axis} />
						<YAxis yAxisId="right" orientation="right" {...axis} />
						<Tooltip />
						<Legend wrapperStyle={{ fontSize: 12 }} />
						<Line
							yAxisId="left"
							name="Impressions"
							type="monotone"
							dataKey="gscImpressions"
							stroke="#6366f1"
							strokeWidth={2}
							dot={false}
						/>
						<Line
							yAxisId="right"
							name="Clicks"
							type="monotone"
							dataKey="gscClicks"
							stroke="#0ea5e9"
							strokeWidth={2}
							dot={false}
						/>
					</LineChart>
				</ResponsiveContainer>
			</Panel>

			<div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
				<Panel title="CTR & average position" hint="position axis inverted">
					<ResponsiveContainer width="100%" height={240}>
						<LineChart
							data={chart}
							margin={{ top: 5, right: 20, bottom: 5, left: -10 }}
						>
							<CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
							<XAxis dataKey="label" {...axis} />
							<YAxis yAxisId="left" unit="%" {...axis} />
							<YAxis
								yAxisId="right"
								orientation="right"
								reversed
								domain={["dataMin - 1", "dataMax + 1"]}
								{...axis}
							/>
							<Tooltip />
							<Legend wrapperStyle={{ fontSize: 12 }} />
							<Line
								yAxisId="left"
								name="CTR %"
								type="monotone"
								dataKey="gscCtr"
								stroke="#10b981"
								strokeWidth={2}
								dot={false}
							/>
							<Line
								yAxisId="right"
								name="Avg. position"
								type="monotone"
								dataKey="gscAvgPosition"
								stroke="#f59e0b"
								strokeWidth={2}
								dot={false}
							/>
						</LineChart>
					</ResponsiveContainer>
				</Panel>

				<Panel title="Partner-ads outbound clicks">
					<ResponsiveContainer width="100%" height={240}>
						<BarChart
							data={chart}
							margin={{ top: 5, right: 20, bottom: 5, left: -10 }}
						>
							<CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
							<XAxis dataKey="label" {...axis} />
							<YAxis {...axis} />
							<Tooltip />
							<Bar
								dataKey="paClicks"
								name="Clicks"
								fill="#10b981"
								radius={[4, 4, 0, 0]}
							/>
						</BarChart>
					</ResponsiveContainer>
				</Panel>
			</div>

			<SocialTraffic days={days} />

			<Panel title="Conversions & commission">
				<ResponsiveContainer width="100%" height={260}>
					<ComposedChart
						data={chart}
						margin={{ top: 5, right: 20, bottom: 5, left: -10 }}
					>
						<CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
						<XAxis dataKey="label" {...axis} />
						<YAxis yAxisId="left" {...axis} />
						<YAxis yAxisId="right" orientation="right" {...axis} />
						<Tooltip />
						<Legend wrapperStyle={{ fontSize: 12 }} />
						<Bar
							yAxisId="left"
							name="Commission (kr)"
							dataKey="paCommission"
							fill="#7367f0"
							radius={[4, 4, 0, 0]}
						/>
						<Line
							yAxisId="right"
							name="Conversions"
							type="monotone"
							dataKey="paConversions"
							stroke="#ff9f43"
							strokeWidth={2}
							dot={false}
						/>
					</ComposedChart>
				</ResponsiveContainer>
			</Panel>

			<Panel
				title="Daily detail"
				hint={data ? `read ${fmtDateTime(data.generatedAt)}` : undefined}
			>
				<div className="max-h-[520px] overflow-auto">
					<table className="w-full text-left text-[13px]">
						<thead className="sticky top-0 bg-white">
							<tr className="border-b border-slate-200 text-slate-500">
								{[
									"Date",
									"Impr.",
									"Clicks",
									"CTR",
									"Pos.",
									"URLs",
									"Outbound",
									"Conv.",
									"Commission",
								].map((h) => (
									<th key={h} className="pb-2 pr-3 font-medium">
										{h}
									</th>
								))}
							</tr>
						</thead>
						<tbody>
							{items.length === 0 && (
								<tr>
									<td colSpan={9} className="py-3 text-slate-400">
										No data yet — run a backfill or wait for the 13:00 job.
									</td>
								</tr>
							)}
							{[...items].reverse().map((d) => (
								<tr
									key={isoDay(d.date)}
									className="border-b border-slate-100 last:border-0"
								>
									<td className="py-2 pr-3 text-slate-700">{isoDay(d.date)}</td>
									<td className="py-2 pr-3 text-slate-700">
										{d.gscImpressions === null ? "—" : fmt(d.gscImpressions)}
									</td>
									<td className="py-2 pr-3 text-slate-700">
										{d.gscClicks === null ? "—" : fmt(d.gscClicks)}
									</td>
									<td className="py-2 pr-3 text-slate-700">
										{d.gscCtr === null ? "—" : `${d.gscCtr.toFixed(2)}%`}
									</td>
									<td className="py-2 pr-3 text-slate-700">
										{d.gscAvgPosition === null
											? "—"
											: d.gscAvgPosition.toFixed(1)}
									</td>
									<td className="py-2 pr-3 text-slate-700">
										{d.gscUrls === null ? "—" : fmt(d.gscUrls)}
									</td>
									<td className="py-2 pr-3 text-slate-700">
										{d.paClicks === null ? "—" : fmt(d.paClicks)}
									</td>
									<td className="py-2 pr-3 text-slate-700">
										{d.paConversions === null ? "—" : fmt(d.paConversions)}
									</td>
									<td className="py-2 pr-3 text-slate-700">
										{d.paCommission === null ? "—" : kr(d.paCommission)}
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			</Panel>
		</div>
	);
};

export default Metrics;
